import axios from "axios";
import { getEnvVar } from "../config/RuntimeConfig";
import {
    FormSections,
    FormSubSections,
    FormSectionElements,
    ProgramContainer,
    LOVValues,
} from "../entities/formTemplateDataType";

interface ScreenshotAnalysis {
    sectionTitle: string;
    sectionDescription?: string;
    isSummaryPage?: boolean;
    elements: ScreenshotElement[];
}

interface ScreenshotElement {
    label: string;
    type: string;
    optional: boolean;
    hint?: string;
    options?: string[];
    htmlContent?: string;
    children?: ScreenshotElement[];
    columns?: { label: string; bindName: string; columnType: string }[];
}

interface ClaudeResponse {
    content: { type: string; text: string }[];
}

/**
 * Maps a field type string from Claude's analysis to the designer element Datatype.
 */
function mapFieldType(type: string): string {
    const normalized = type.toLowerCase();
    if (normalized === "row") return "Row";
    if (normalized.includes("dropdown") || normalized.includes("select")) return "Dropdown";
    if (normalized.includes("checkbox") && normalized.includes("inline")) return "CheckboxWithInlineTextbox";
    if (normalized.includes("checkgroup") || normalized.includes("multiple choice")) return "CheckGroup";
    if (normalized.includes("checkbox")) return "Checkbox";
    if (normalized.includes("radio") && normalized.includes("rich")) return "RadioButtonWithRichText";
    if (normalized.includes("radio")) return "RadioButton";
    if (normalized.includes("date")) return "Date";
    if (normalized.includes("email") && normalized.includes("button")) return "EmailButton";
    if (normalized.includes("email") && normalized.includes("confirm")) return "EmailConfirmation";
    if (normalized.includes("email")) return "Email";
    if (normalized.includes("phone") || normalized.includes("tel")) return "PhoneNumber";
    if (normalized.includes("textarea") || normalized.includes("multitext")) return "MultiText";
    if (normalized.includes("number") || normalized.includes("numeric") || normalized.includes("currency")) return "Number";
    if (normalized.includes("file") || normalized.includes("upload")) return "File";
    if (normalized.includes("address")) return "Address";
    if (normalized.includes("postalcode") || normalized.includes("postal code")) return "PostalCode";
    if (normalized.includes("richtext") || normalized.includes("rich text") || normalized.includes("label")) return "Label";
    if (normalized.includes("html")) return "HTML";
    if (normalized.includes("callout") || normalized.includes("info alert")) return "Callout";
    if (normalized.includes("warning")) return "WarningAlert";
    if (normalized.includes("error alert")) return "ErrorAlert";
    if (normalized.includes("success alert")) return "SuccessAlert";
    if (normalized.includes("information alert")) return "InformationAlert";
    if (normalized.includes("heading") || normalized.includes("title")) return "Label";
    if (normalized.includes("time") && normalized.includes("estimate")) return "TimeEstimate";
    if (normalized.includes("pagedsummarylist") || normalized.includes("paged summary list")) return "PagedSummaryList";
    if (normalized.includes("datatable") || normalized.includes("data table") || normalized.includes("table")) return "PagedSummaryList";
    if (normalized.includes("button")) return "Button";
    return "Text";
}

/**
 * Reads screenshot images via the Claude API and returns structured form section data.
 */
export async function analyzeScreenshots(files: File[], additionalPrompt?: string): Promise<ScreenshotAnalysis[]> {
    const apiKey = getEnvVar("CLAUDE_API_KEY");
    if (!apiKey) {
        throw new Error("CLAUDE_API_KEY is not configured. Add it to your .env.local file.");
    }

    // Convert files to base64
    const imageContents = await Promise.all(
        files.map(
            (file) =>
                new Promise<{ base64: string; mediaType: string }>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const result = reader.result as string;
                        const base64 = result.split(",")[1];
                        const mediaType = file.type || "image/png";
                        resolve({ base64, mediaType });
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                })
        )
    );

    // Build Claude API message with images
    const imageBlocks = imageContents.map((img) => ({
        type: "image" as const,
        source: {
            type: "base64" as const,
            media_type: img.mediaType,
            data: img.base64,
        },
    }));

    const payload = {
        model: "claude-sonnet-4-20250514",
        max_tokens: 4096,
        temperature: 0,
        system: "You are a strict OCR transcription tool. You ONLY describe what is literally visible in the provided images. You NEVER add, infer, or hallucinate any content not visually present. If something is not clearly shown in the screenshot, you omit it entirely.",
        messages: [
            {
                role: "user",
                content: [
                    ...imageBlocks,
                    {
                        type: "text",
                        text: `STRICT RULE: You are an OCR tool. ONLY output what you can LITERALLY SEE in the provided screenshot(s). Do NOT add, infer, guess, or hallucinate ANY content that is not visually present in the image. If a field, heading, paragraph, or section is not visible in the screenshot, it MUST NOT appear in your output. Treat this as an absolute constraint.

You are analyzing screenshots of an application form to recreate it in a form builder.

A single screenshot may show MULTIPLE sections/steps. Look for step indicators, section headers, dividers, or progress bars to identify separate sections.

Return a JSON array where each object represents one SECTION visible in the image:

[
  {
    "sectionTitle": "Section/step name visible on screen",
    "sectionDescription": "Any description text shown for this section",
    "isSummaryPage": false,
    "elements": [
      {
        "label": "Field label text",
        "type": "Text|Row|Dropdown|Checkbox|RadioButton|Date|Email|PhoneNumber|MultiText|Number|File|Address|RichText|HTML|TimeEstimate|Callout|InformationAlert|WarningAlert|Button|PagedSummaryList",
        "optional": false,
        "hint": "Any hint or helper text shown",
        "options": ["option1", "option2"],
        "htmlContent": "<p>Full HTML content here</p>",
        "children": [],
        "columns": []
      }
    ]
  }
]

TYPE RULES:
- "Text" = single-line text input
- "Row" = container for fields side by side on the SAME row. Put each field in "children" array.
- "MultiText" = multi-line text area
- "Dropdown" = dropdown list. Include "options".
- "RadioButton" = radio group. Include "options".
- "Checkbox" = single checkbox
- "CheckGroup" = multiple checkboxes. Include "options".
- "Date" = date picker
- "Email" = email input
- "PhoneNumber" = phone number
- "Number" = numeric input
- "File" = file upload. Use this when you see an "Attach" button or an attachments section.
- "Address" = full address block. CRITICAL: When you see ANY combination of these fields grouped together, output a SINGLE element with type "Address" and a descriptive label (e.g. "Service Location Address" or "Mailing Address"): unit number, street number, street name, street type, PO box, postal station, rural route, lot/plan, city, town, municipality, province, postal code, country. Do NOT output individual Text fields for these — use ONE "Address" element.
- "PostalCode" = standalone postal/zip code (only use when postal code appears ALONE, not part of an address group)
- "Button" = action button
- "RichText" = static/read-only content (headings, paragraphs, instructions, bullet lists). Provide HTML in "htmlContent". Use <ol><li> for numbered lists and <ul><li> for bullet lists — each item on its own line.
- "TimeEstimate" = time estimate callout (e.g. "This form takes approximately 15 minutes to complete"). Use this whenever you see an estimated time/duration to complete the form. Provide the time estimate text in "htmlContent".
- "Callout" = callout/highlight box
- "InformationAlert" / "WarningAlert" = alert boxes
- "PagedSummaryList" = data table with rows and columns, pagination, sorting. Use when you see a table/grid/list of records with column headers and data rows. The element's "label" should be the table title/heading. Leave "columns" as an empty array — sample columns will be auto-generated for the designer to customize. Ignore pagination controls — they are auto-generated.

KEY RULES:
- Return ONLY the JSON array. No markdown, no code fences, no extra text.
- IGNORE step indicators ("Step 1 of 10", etc.) — auto-generated by form builder.
- IGNORE navigation buttons ("Continue", "Back", "Next") — auto-generated.
- Address fields grouped together (unit number, street number, street name, city, province, postal code, PO box, etc.) → MUST be a single "Address" element. NEVER output them as individual Text fields.
- "Attach" button or attachments section → create a RichText element with the instructions as htmlContent (using <ol><li> for numbered steps and <ul><li> for bullet points), THEN a separate "File" element after it.
- Side-by-side fields → wrap in "Row" with children.
- Data tables / record lists with column headers and rows → use "PagedSummaryList". IGNORE pagination controls (page numbers, next/previous buttons) — they are auto-generated.
- For informational pages, combine text into fewer RichText elements with HTML.
- "optional": true only if field has no asterisk or says "optional".
- If a section is a "Declaration", "Review", "Summary", "Review and Submit", or "Confirmation" page (where the user reviews previously entered data before submitting), set "isSummaryPage": true. Also set "isSummaryPage": true if the user mentions "VECON", "CHC", "summary like VECON", "summary like CHC", or "review and submit" in additional instructions — these mean the screenshot shows a review/summary page. For summary pages, the review cards that repeat previous sections will be auto-generated from existing form data — do NOT include elements for those review cards. ONLY include elements that are NEW interactive content on the summary page that is NOT a review card (e.g., a "Terms of agreement" heading, disclaimer text, a consent checkbox). If the summary page has no new interactive content beyond the review cards, return an empty elements array. Do NOT output review cards or their fields — only declaration/terms/consent content.
- In htmlContent AND in labels, make email addresses clickable with mailto links: <a href="mailto:user@example.com">user@example.com</a>. Make URLs clickable with: <a href="https://..." target="_blank">link text</a>. Make phone numbers clickable with tel links: <a href="tel:1-888-444-1116">1-888-444-1116</a>. Apply this everywhere text appears, not just htmlContent.
- NEVER output anything not visible in the screenshot. This is the most important rule.${additionalPrompt ? `\n\nADDITIONAL INSTRUCTIONS FROM USER:\n${additionalPrompt}` : ''}`,
                    },
                ],
            },
        ],
    };

    const response = await axios.post<ClaudeResponse>(
        "https://api.anthropic.com/v1/messages",
        payload,
        {
            headers: {
                "x-api-key": apiKey,
                "anthropic-version": "2023-06-01",
                "content-type": "application/json",
                "anthropic-dangerous-direct-browser-access": "true",
            },
            timeout: 120000,
        }
    );

    const textContent = response.data.content.find((c) => c.type === "text");
    if (!textContent) {
        throw new Error("No text response from Claude API");
    }

    // Strip markdown code fences if present
    let jsonText = textContent.text.trim();
    if (jsonText.startsWith("```")) {
        jsonText = jsonText.replace(/^```(?:json)?\s*/, "").replace(/\s*```$/, "");
    }

    const parsed: ScreenshotAnalysis[] = JSON.parse(jsonText);

    // Fix any inline numbered/bullet lists in htmlContent
    const fixed = fixListFormatting(parsed);

    return fixed;
}

/**
 * Post-processes analyses to convert inline numbered/bullet lists in htmlContent
 * into proper HTML <ol>/<ul> elements so each item renders on its own line.
 */
function fixListFormatting(analyses: ScreenshotAnalysis[]): ScreenshotAnalysis[] {
    for (const section of analyses) {
        for (const el of section.elements) {
            if (el.htmlContent) {
                el.htmlContent = convertInlineListsToHtml(el.htmlContent);
            }
            if (el.hint) {
                el.hint = convertInlineListsToHtml(el.hint);
            }
            if (el.children) {
                for (const child of el.children) {
                    if (child.htmlContent) {
                        child.htmlContent = convertInlineListsToHtml(child.htmlContent);
                    }
                    if (child.hint) {
                        child.hint = convertInlineListsToHtml(child.hint);
                    }
                }
            }
        }
    }
    return analyses;
}

/**
 * Converts inline numbered lists (1. xxx 2. xxx) and bullet lists (• xxx • xxx)
 * into proper <ol><li> or <ul><li> HTML.
 */
function convertInlineListsToHtml(html: string): string {
    // If it already has proper <ol> or <ul> tags, skip
    if (/<[ou]l[\s>]/i.test(html)) return html;

    let result = html;

    // Strip HTML tags for analysis, keep original for output
    const plainText = result.replace(/<[^>]+>/g, ' ');

    // Detect sequential numbered items: "1. xxx 2. xxx 3. xxx"
    // Only match numbers that look like list counters (sequential from 1, or at least 3+ items)
    const numberedItemRegex = /(?:^|\s)(\d+)\.\s+/g;
    const positions: { num: number; index: number }[] = [];
    let match;
    while ((match = numberedItemRegex.exec(plainText)) !== null) {
        positions.push({ num: parseInt(match[1]), index: match.index });
    }

    // Check if these form a sequential list (1, 2, 3... or at least starts with 1)
    if (positions.length >= 2 && positions[0].num === 1) {
        // Strip wrapping tags
        let inner = result.replace(/^<p>/i, '').replace(/<\/p>$/i, '');

        // Split on the numbered markers — use a pattern that matches "N. " only when N is sequential
        // Build a regex that splits on each expected number
        let olItems: string[] = [];
        let remaining = inner;
        let noteSection = '';

        for (let i = positions.length; i >= 1; i--) {
            const marker = new RegExp(i + '\\.\\s+');
            const idx = remaining.search(marker);
            if (idx >= 0) {
                const afterMarker = remaining.substring(idx).replace(marker, '');
                // Check if there's a "Note:" section at the end
                const noteIdx = afterMarker.search(/Note:\s*/i);
                if (i === positions.length && noteIdx >= 0) {
                    noteSection = afterMarker.substring(noteIdx);
                    olItems.unshift(afterMarker.substring(0, noteIdx).trim());
                } else {
                    olItems.unshift(afterMarker.trim());
                }
                remaining = remaining.substring(0, idx);
            }
        }

        // Any text before "1." is a prefix
        const prefix = remaining.trim();
        if (olItems.length >= 2) {
            result = (prefix ? '<p>' + prefix + '</p>' : '') +
                '<ol>' + olItems.map((item) => '<li>' + item + '</li>').join('') + '</ol>';

            // Handle Note: section with bullets
            if (noteSection) {
                result += convertBulletsToUl(noteSection);
            }
        }
    }

    // Handle standalone bullet lists (• character)
    if (!/<[ou]l[\s>]/i.test(result)) {
        result = convertBulletsToUl(result);
    }

    return result;
}

/**
 * Converts bullet point text (using • character) into <ul><li> HTML.
 */
function convertBulletsToUl(text: string): string {
    const bulletCount = (text.match(/•/g) || []).length;
    if (bulletCount < 2) return text;

    let inner = text.replace(/^<p>/i, '').replace(/<\/p>$/i, '');
    const firstBulletIdx = inner.indexOf('•');
    const beforeBullets = inner.substring(0, firstBulletIdx).trim();
    const bulletPart = inner.substring(firstBulletIdx);
    const items = bulletPart.split('•').filter((p) => p.trim());

    if (items.length >= 2) {
        const prefix = beforeBullets ? '<p>' + beforeBullets + '</p>' : '';
        return prefix + '<ul>' + items.map((item) => '<li>' + item.trim() + '</li>').join('') + '</ul>';
    }
    return text;
}

/**
 * Applies type-specific default validation settings to a form element.
 */
function applyDefaultValidation(element: FormSectionElements): void {
    switch (element.Datatype) {
        case 'Email':
            element.RegularExpression = '^[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$';
            element.ErrorMessage = '{0} must be a valid email address.';
            element.ErrorMessageFRA = '{0} doit être une adresse courriel valide.';
            break;
        case 'PhoneNumber':
            element.ErrorMessage = '{0} must be a valid phone number.';
            element.ErrorMessageFRA = '{0} doit être un numéro de téléphone valide.';
            break;
        case 'Number':
            element.ErrorMessage = '{0} must be a valid number.';
            element.ErrorMessageFRA = '{0} doit être un nombre valide.';
            break;
        case 'Date':
            element.ErrorMessage = '{0} must be a valid date.';
            element.ErrorMessageFRA = '{0} doit être une date valide.';
            break;
        case 'PostalCode':
            element.RegularExpression = '^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$';
            element.ErrorMessage = '{0} must be a valid postal code.';
            element.ErrorMessageFRA = '{0} doit être un code postal valide.';
            break;
    }
}

/**
 * Generates a binding field name from the element label using double-underscore format.
 * Matches the designer convention: "First Name" → "First__Name".
 */
function generateBindingName(label: string): string {
    // Strip HTML tags, trim, and join words with "__"
    return label.replace(/<[^>]+>/g, '').trim().split(/\s+/).join('__');
}

/**
 * Creates a single FormSectionElements from a screenshot element definition.
 */
/**
 * Wraps plain email addresses, URLs, and phone numbers in HTML links.
 * Already-linked content (inside <a> tags) is left untouched.
 */
function linkifyText(text: string): string {
    if (!text) return text;
    // Split on existing <a ...>...</a> tags to avoid double-linking
    const parts = text.split(/(<a\s[^>]*>.*?<\/a>)/gi);
    return parts.map(part => {
        // If this part is already an <a> tag, leave it alone
        if (/^<a\s/i.test(part)) return part;
        // Email: word@word.word
        let result = part.replace(
            /\b([A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,})\b/g,
            '<a href="mailto:$1">$1</a>'
        );
        // URL: http(s)://... or www.
        result = result.replace(
            /\b(https?:\/\/[^\s<>"]+)/gi,
            '<a href="$1" target="_blank">$1</a>'
        );
        // Phone: patterns like 1-888-444-1116, (416) 555-1234, 416-555-1234
        result = result.replace(
            /\b((?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4})\b/g,
            (match) => {
                const digits = match.replace(/[^\d+]/g, '');
                return `<a href="tel:${digits}">${match}</a>`;
            }
        );
        return result;
    }).join('');
}

function createElement(el: ScreenshotElement, idx: number, parent: FormSubSections): FormSectionElements {
    const element = new FormSectionElements();
    element.Sequence = idx + 1;
    element.Id = (idx + 1).toString();
    element.ENUTitle = el.label;
    element.FRATitle = el.label;
    element.Datatype = mapFieldType(el.type);
    element.Optional = el.optional ? "Y" : "N";
    element.Visible = true;
    element.Editable = "Y";
    element.parent = parent;

    // Generate binding field name (LoadBindName) matching designer convention: words joined by "__"
    element.LoadBindName = generateBindingName(el.label);

    // Enable default validation for required fields (mirrors designer "Required" checkbox behavior)
    if (!el.optional) {
        element.EmptyErrorMessage = '{0} is a mandatory field';
        element.EmptyErrorMessageFRA = '{0} est un champ obligatoire.';
    }

    // Set type-specific default validation
    applyDefaultValidation(element);

    if (el.hint) {
        element.Hint = el.hint;
        element.HintFR = el.hint;
    }

    // For TimeEstimate type, use Label datatype with timeEstimate callout wrapper
    if (element.Datatype === 'TimeEstimate') {
        element.Datatype = 'Label';
        element.wrapCalloutbox = 'timeEstimate';
        if (el.htmlContent) {
            element.Value = el.htmlContent;
            element.ValueFRA = el.htmlContent;
        }
    }

    // For Address type, set sensible defaults so the AddressComponent renders correctly
    if (element.Datatype === 'Address') {
        element.addressType = 'Residential';
        element.allowManualAddress = true;
        element.isBXPaddress = false;
    }

    // For PagedSummaryList type, configure table with 3 sample columns
    if (element.Datatype === 'PagedSummaryList') {
        element.SummaryType = 'Table';
        element.ShowPagination = true;
        element.PageSize = 10;
        element.Editable = "N";

        const colSubSection = new FormSubSections();
        colSubSection.Sequence = 1;
        colSubSection.ENUTitle = "";
        colSubSection.FRATitle = "";
        colSubSection.parent = null;
        colSubSection.Visible = true;
        colSubSection.ListOfFormSectionElements = [];

        const sampleCols = [
            { label: 'Column 1', bindName: 'col1' },
            { label: 'Column 2', bindName: 'col2' },
            { label: 'Column 3', bindName: 'col3' },
        ];
        sampleCols.forEach((col, colIdx) => {
            const colEl = new FormSectionElements();
            colEl.Sequence = colIdx + 1;
            colEl.Id = (colIdx + 1).toString();
            colEl.ENUTitle = col.label;
            colEl.FRATitle = col.label;
            colEl.LoadBindName = col.bindName;
            colEl.Datatype = 'Text';
            colEl.Visible = true;
            colEl.Editable = "N";
            colEl.Optional = "Y";
            colEl.AllowSorting = true;
            colEl.ToDisplayWhenEmpty = '-';
            colEl.ToDisplayWhenEmptyFRA = '-';
            colEl.parent = colSubSection;
            colSubSection.ListOfFormSectionElements.push(colEl);
        });

        element.subSectionList = [colSubSection];
    }

    // For RichText/Label/HTML/Callout types, populate Value with HTML content
    const mappedType = element.Datatype;
    if (el.htmlContent && (mappedType === 'Label' || mappedType === 'HTML' || mappedType === 'Callout' || mappedType === 'InformationAlert' || mappedType === 'WarningAlert')) {
        element.Value = el.htmlContent;
        element.ValueFRA = el.htmlContent;
    }

    // Populate dropdown/radio options
    if (el.options && el.options.length > 0) {
        element.ListOfLOVValues = el.options.map((opt, optIdx) => {
            const lov = new LOVValues();
            lov.code = opt;
            lov.description = opt;
            lov.FRA = opt;
            lov.FRADescription = opt;
            lov.Sequence = optIdx + 1;
            lov.Visible = true;
            return lov;
        });
    }

    // Make emails, URLs, and phone numbers clickable in all text fields
    element.ENUTitle = linkifyText(element.ENUTitle);
    element.FRATitle = linkifyText(element.FRATitle);
    if (element.Value && typeof element.Value === 'string') {
        element.Value = linkifyText(element.Value);
    }
    if (element.ValueFRA && typeof element.ValueFRA === 'string') {
        element.ValueFRA = linkifyText(element.ValueFRA);
    }
    if (element.Hint) {
        element.Hint = linkifyText(element.Hint);
    }
    if (element.HintFR) {
        element.HintFR = linkifyText(element.HintFR);
    }

    return element;
}

/**
 * Address-related field labels that should be collapsed into a single Address element.
 */
const ADDRESS_FIELD_PATTERNS = [
    /\bunit\s*(number|#|no\.?|num)?\b/i,
    /\bstreet\s*(number|#|no\.?|num|name|type|direction|suffix)?\b/i,
    /\bp\.?o\.?\s*box\b/i,
    /\bpostal\s*(station|code)\b/i,
    /\brural\s*route\b/i,
    /\bcity\b/i,
    /\btown\b/i,
    /\bmunicipality\b/i,
    /\bprovince\b/i,
    /\bcountry\b/i,
    /\blot\s*(number|type)?\b/i,
    /\baddress\s*(line)?\s*\d?\b/i,
    /\bsuite\b/i,
    /\bapt\.?\b/i,
    /\broute\s*(type|number)\b/i,
];

function isAddressField(label: string): boolean {
    const normalized = label.replace(/<[^>]+>/g, '').trim();
    return ADDRESS_FIELD_PATTERNS.some(pattern => pattern.test(normalized));
}

/**
 * Detects consecutive address-related fields and collapses them into a single Address element.
 */
function collapseAddressFields(elements: ScreenshotElement[]): ScreenshotElement[] {
    const result: ScreenshotElement[] = [];
    let i = 0;

    while (i < elements.length) {
        // Skip elements already typed as Address
        if (elements[i].type.toLowerCase() === 'address') {
            result.push(elements[i]);
            i++;
            continue;
        }

        // Check if this field looks like an address field
        if (isAddressField(elements[i].label)) {
            // Gather consecutive address-like fields (only collapse Text, Dropdown, PostalCode, Number types)
            const addressStart = i;
            const collectedLabels: string[] = [];
            while (i < elements.length && isAddressField(elements[i].label) &&
                   (elements[i].type.toLowerCase() === 'text' || elements[i].type.toLowerCase() === 'dropdown' ||
                    elements[i].type.toLowerCase() === 'postalcode' || elements[i].type.toLowerCase() === 'number')) {
                collectedLabels.push(elements[i].label.replace(/<[^>]+>/g, '').trim());
                i++;
            }

            // If we found 2+ address fields in a row, collapse them
            if (collectedLabels.length >= 2) {
                const addressLabel = collectedLabels.join(', ');
                const isOptional = elements.slice(addressStart, addressStart + collectedLabels.length).every(el => el.optional);
                result.push({
                    label: addressLabel,
                    type: 'Address',
                    optional: isOptional,
                });
                console.log(`Collapsed ${collectedLabels.length} address fields into single Address: [${collectedLabels.join(', ')}]`);
            } else {
                // No fields were collected or only one — keep original and advance
                result.push(elements[addressStart]);
                if (i === addressStart) {
                    i++;
                }
            }
        } else {
            // Handle Row children recursively
            if (elements[i].type.toLowerCase() === 'row' && elements[i].children?.length) {
                elements[i].children = collapseAddressFields(elements[i].children);
            }
            result.push(elements[i]);
            i++;
        }
    }

    return result;
}

/**
 * For a summary/review page, builds subsections by mirroring existing editable sections
 * in the ProgramContainer. Each editable section becomes a card with its fields displayed
 * as read-only info elements and a "Change" link to navigate back.
 */
function buildSummarySubsections(
    section: FormSections,
    editableSections: FormSections[],
    analysisElements: ScreenshotElement[],
    options?: { chcStyle?: boolean }
): void {
    const subSections: FormSubSections[] = [];
    let subSeq = 1;

    // Create a subsection for each existing editable section
    for (const srcSection of editableSections) {
        const sub = new FormSubSections();
        sub.Sequence = subSeq++;
        sub.ENUTitle = srcSection.ENUTitle;
        sub.FRATitle = srcSection.FRATitle || srcSection.ENUTitle;
        sub.parent = section;
        sub.Visible = true;
        sub.ListOfFormSectionElements = [];

        // StepperScript navigates to the original section by title
        const escapedTitle = (srcSection.ENUTitle || '').replace(/'/g, "\\'");
        sub.StepperScript = `(navigate) => { navigate('${escapedTitle}'); }`;

        // CHC-style: hide header change link, add a Button element at card bottom instead
        if (options?.chcStyle) {
            sub.HideChangeLink = true;
        }

        // Mirror each element from the source section's subsections
        let elSeq = 1;
        for (const srcSub of (srcSection.ListOfFormSubSections || [])) {
            for (const srcEl of (srcSub.ListOfFormSectionElements || [])) {
                // Skip non-data elements (labels, hidden fields, buttons, alerts, callouts, etc.)
                if (['Label', 'HTML', 'Button', 'HiddenField', 'Callout',
                     'InformationAlert', 'WarningAlert', 'ErrorAlert', 'SuccessAlert',
                     'TimeEstimate'].includes(srcEl.Datatype)) {
                    continue;
                }
                // Skip Row containers but include their children
                if (srcEl.Datatype === 'Row' && srcEl.subSectionList?.length) {
                    for (const rowSub of srcEl.subSectionList) {
                        for (const rowEl of (rowSub.ListOfFormSectionElements || [])) {
                            if (['Label', 'HTML', 'Button', 'HiddenField'].includes(rowEl.Datatype)) continue;
                            const mirror = new FormSectionElements();
                            mirror.Sequence = elSeq++;
                            mirror.Id = elSeq.toString();
                            mirror.ENUTitle = rowEl.ENUTitle;
                            mirror.FRATitle = rowEl.FRATitle || rowEl.ENUTitle;
                            mirror.LoadBindName = rowEl.LoadBindName;
                            mirror.Datatype = rowEl.Datatype;
                            mirror.Visible = true;
                            mirror.Editable = "N";
                            mirror.Optional = rowEl.Optional || "Y";
                            mirror.parent = sub;
                            if (options?.chcStyle) {
                                mirror.MaskInSummary = true;
                                mirror.mask = true;
                            }
                            if (rowEl.LoadBindName) {
                                const escaped = rowEl.LoadBindName.replace(/'/g, "\\'");
                                mirror.Script = `(field, env) => { const src = field.findFieldByName('${escaped}'); if (src) { field.Value = src.Value; } }`;
                            }
                            sub.ListOfFormSectionElements.push(mirror);
                        }
                    }
                    continue;
                }

                const mirror = new FormSectionElements();
                mirror.Sequence = elSeq++;
                mirror.Id = elSeq.toString();
                mirror.ENUTitle = srcEl.ENUTitle;
                mirror.FRATitle = srcEl.FRATitle || srcEl.ENUTitle;
                mirror.LoadBindName = srcEl.LoadBindName;
                mirror.Datatype = srcEl.Datatype;
                mirror.Visible = true;
                mirror.Editable = "N";
                mirror.Optional = srcEl.Optional || "Y";
                mirror.parent = sub;
                if (options?.chcStyle) {
                    mirror.MaskInSummary = true;
                    mirror.mask = true;
                }
                if (srcEl.LoadBindName) {
                    const escaped = srcEl.LoadBindName.replace(/'/g, "\\'");
                    mirror.Script = `(field, env) => { const src = field.findFieldByName('${escaped}'); if (src) { field.Value = src.Value; } }`;
                }
                sub.ListOfFormSectionElements.push(mirror);
            }
        }

        // Skip sections that produced no editable/data fields (e.g. info-only pages)
        if (sub.ListOfFormSectionElements.length === 0) {
            continue;
        }

        // CHC-style: add "Edit [section name]" Button at the bottom of each card
        if (options?.chcStyle) {
            const sectionName = (srcSection.ENUTitle || '').replace(/<[^>]+>/g, '').trim().toLowerCase();
            const editBtn = new FormSectionElements();
            editBtn.Sequence = elSeq++;
            editBtn.Id = elSeq.toString();
            editBtn.ENUTitle = `Edit ${sectionName}`;
            editBtn.FRATitle = `Modifier ${sectionName}`;
            editBtn.Datatype = 'Button';
            editBtn.Visible = true;
            editBtn.Editable = "Y";
            editBtn.Optional = "Y";
            editBtn.parent = sub;
            editBtn.StepperScript = `(navigate) => { navigate('${escapedTitle}'); }`;
            sub.ListOfFormSectionElements.push(editBtn);
        }

        subSections.push(sub);
    }

    // Declaration/terms elements go into a NoCardInSummary subsection below all read-only cards
    if (analysisElements && analysisElements.length > 0) {
        const declSub = new FormSubSections();
        declSub.Sequence = subSeq++;
        declSub.ENUTitle = "";
        declSub.FRATitle = "";
        declSub.parent = section;
        declSub.Visible = true;
        declSub.NoCardInSummary = true;
        declSub.ListOfFormSectionElements = [];

        const collapsedElements = collapseAddressFields(analysisElements);
        collapsedElements.forEach((el, idx) => {
            const element = createElement(el, idx, declSub);
            if (element.Datatype === 'Row' && el.children && el.children.length > 0) {
                const rowSubSection = new FormSubSections();
                rowSubSection.Sequence = 1;
                rowSubSection.parent = element;
                rowSubSection.ListOfFormSectionElements = [];
                el.children.forEach((child, childIdx) => {
                    const childElement = createElement(child, childIdx, rowSubSection);
                    rowSubSection.ListOfFormSectionElements.push(childElement);
                });
                element.subSectionList = [rowSubSection];
            }
            declSub.ListOfFormSectionElements.push(element);
        });

        if (declSub.ListOfFormSectionElements.length > 0) {
            subSections.push(declSub);
        }
    }

    section.ListOfFormSubSections = subSections;
}

/**
 * Populates a ProgramContainer with sections derived from screenshot analysis.
 */
export function populateFormFromAnalysis(
    programContainer: ProgramContainer,
    analyses: ScreenshotAnalysis[],
    additionalPrompt?: string
): ProgramContainer {
    const summaryKeywords = /\b(summary|review|summaries|vecon|chc|review\s*and\s*submit)\b/i;
    const userRequestedSummary = !!(additionalPrompt && summaryKeywords.test(additionalPrompt));
    const isChcStyle = !!(additionalPrompt && /\bchc\b/i.test(additionalPrompt));
    const existingSections = programContainer.ListOfFormSections || [];
    let nextSequence =
        existingSections.length > 0
            ? Math.max(...existingSections.map((s) => s.Sequence || 0)) + 1
            : 1;
    let nextId =
        existingSections.length > 0
            ? Math.max(...existingSections.map((s) => parseInt(s.Id) || 0)) + 1
            : 1;

    for (const analysis of analyses) {
        const section = new FormSections();
        section.Id = nextId.toString();
        section.ENUTitle = linkifyText(analysis.sectionTitle);
        section.FRATitle = linkifyText(analysis.sectionTitle);
        section.ENUDescription = linkifyText(analysis.sectionDescription || "");
        section.FRADescription = linkifyText(analysis.sectionDescription || "");
        section.Sequence = nextSequence;
        section.Visible = true;
        section.parent = programContainer;

        if (analysis.isSummaryPage && userRequestedSummary) {
            section.CustomizedReviewPage = true;
            section.followSubSectionOrder = true;
            section.CustomizedSummaryProps = {
                changeLinkInSubsectionLevel: true,
            };

            // Find all existing editable sections (exclude special pages)
            const editableSections = existingSections.filter(s =>
                !s.CustomizedReviewPage &&
                !s.IsTerminationPage &&
                !s.IsFrontPage &&
                !s.IsConfirmationPage &&
                !s.IsTechErrorPage &&
                s.Visible !== false
            );

            if (editableSections.length > 0) {
                buildSummarySubsections(section, editableSections, analysis.elements, { chcStyle: isChcStyle });
                console.log(`Summary page "${analysis.sectionTitle}": mirrored ${editableSections.length} editable sections`);
            } else {
                // No editable sections found — fall back to Claude's elements
                const subSection = new FormSubSections();
                subSection.Sequence = 1;
                subSection.ENUTitle = "Main";
                subSection.FRATitle = "Main";
                subSection.parent = section;
                subSection.ListOfFormSectionElements = [];

                const collapsedElements = collapseAddressFields(analysis.elements);
                collapsedElements.forEach((el, idx) => {
                    const element = createElement(el, idx, subSection);
                    if (element.Datatype === 'Row' && el.children && el.children.length > 0) {
                        const rowSubSection = new FormSubSections();
                        rowSubSection.Sequence = 1;
                        rowSubSection.parent = element;
                        rowSubSection.ListOfFormSectionElements = [];
                        el.children.forEach((child, childIdx) => {
                            const childElement = createElement(child, childIdx, rowSubSection);
                            rowSubSection.ListOfFormSectionElements.push(childElement);
                        });
                        element.subSectionList = [rowSubSection];
                    }
                    subSection.ListOfFormSectionElements.push(element);
                });

                section.ListOfFormSubSections = [subSection];
            }
        } else {
            const subSection = new FormSubSections();
            subSection.Sequence = 1;
            subSection.ENUTitle = "Main";
            subSection.FRATitle = "Main";
            subSection.parent = section;
            subSection.ListOfFormSectionElements = [];

            // Collapse individual address fields into single Address elements
            const collapsedElements = collapseAddressFields(analysis.elements);

            collapsedElements.forEach((el, idx) => {
                const element = createElement(el, idx, subSection);

                // If this is a Row, build children inside subSectionList
                if (element.Datatype === 'Row' && el.children && el.children.length > 0) {
                    const rowSubSection = new FormSubSections();
                    rowSubSection.Sequence = 1;
                    rowSubSection.parent = element;
                    rowSubSection.ListOfFormSectionElements = [];

                    el.children.forEach((child, childIdx) => {
                        const childElement = createElement(child, childIdx, rowSubSection);
                        rowSubSection.ListOfFormSectionElements.push(childElement);
                    });

                    element.subSectionList = [rowSubSection];
                }

                subSection.ListOfFormSectionElements.push(element);
            });

            section.ListOfFormSubSections = [subSection];
        }

        existingSections.push(section);

        nextSequence++;
        nextId++;
    }

    programContainer.ListOfFormSections = existingSections;
    return programContainer;
}
